/**
 * MagmaRightPseudoBox Component
 *
 * Magma skin right pseudo box with green gradient surface, geometric faceted purple
 * frame, and green sparkle decorations. Displays player username with truncation.
 */

import { memo } from 'react';
import type { PseudoBoxProps } from '../basics/constants/types';
import { buildClassNames } from '@/utils/classNames';
import { formatPseudoForDisplay, PSEUDO_BOX_CONFIG } from '../basics/utils/utils';
import { MAGMA_PSEUDO_VIEWBOX } from './constants';

const MagmaRightPseudoBox = memo(function MagmaRightPseudoBox({
  pseudo,
  viewBox = MAGMA_PSEUDO_VIEWBOX,
  className = ''
}: PseudoBoxProps) {
  const svgClassName = buildClassNames(className);
  const displayText = formatPseudoForDisplay(pseudo);

  return (
    <svg
      viewBox={viewBox}
      className={svgClassName}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Player username display box - magma right position"
      focusable="false"
    >
      <defs>
        {/* Dark bottom shadow base */}
        <linearGradient
          id="mgmRGrad0"
          x1="153.1"
          y1="79.05"
          x2="153.1"
          y2="104.56"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#2A1C35" />
          <stop offset="1" />
        </linearGradient>
        {/* Purple mid-layer accent */}
        <linearGradient
          id="mgmRGrad1"
          x1="153.12"
          y1="68.5101"
          x2="153.12"
          y2="95.7001"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#886F97" />
          <stop offset="1" />
        </linearGradient>
        {/* Green surface gradient */}
        <linearGradient
          id="mgmRGrad2"
          x1="4.3999"
          y1="41.7499"
          x2="301.63"
          y2="41.7499"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#128406" />
          <stop offset="1" stopColor="#2BBF02" />
        </linearGradient>
        {/* Green surface gradient - top left corner */}
        <linearGradient
          id="mgmRGrad3"
          x1="9.6099"
          y1="33.23"
          x2="31.5599"
          y2="12.5701"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#29B208" />
          <stop offset="0.52" stopColor="#7DFF0B" />
          <stop offset="1" stopColor="#25C909" />
        </linearGradient>
        {/* Green surface gradient - left facet */}
        <linearGradient
          id="mgmRGrad4"
          x1="43.93"
          y1="62.32"
          x2="74.17"
          y2="33.85"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#29B208" />
          <stop offset="0.52" stopColor="#7DFF0B" />
          <stop offset="1" stopColor="#25C909" />
        </linearGradient>
        {/* Green surface gradient - top right facet */}
        <linearGradient
          id="mgmRGrad5"
          x1="154.39"
          y1="19.62"
          x2="288.47"
          y2="19.62"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#29B208" />
          <stop offset="0.52" stopColor="#7DFF0B" />
          <stop offset="1" stopColor="#25C909" />
        </linearGradient>
        {/* Text clip path */}
        <clipPath id="mgmRTextClip">
          <rect x="15" y="5" width="277" height="75" rx="20" />
        </clipPath>
      </defs>

      {/* Layer 1: Dark bottom shadow base */}
      <path
        d="M4.39988 48.7299V74.16C4.39988 81.6 8.37988 88.5499 14.9399 92.07C14.9899 92.0999 15.0399 92.13 15.0999 92.15C22.6999 96.17 29.8699 95.2399 38.5399 94.8399C49.0999 94.3499 59.6699 95.9 70.2399 95.9C91.3099 95.9 112.38 95.93 133.45 95.9C148.6 95.88 163.52 95.9 174.18 95.9C190.52 95.91 198.69 95.92 199.54 95.94C235.44 96.5 238.3 98.93 251.03 95.94C259.83 93.87 262.98 91.6499 271.2 91.7699C278.99 91.8799 283.98 93.9799 290.41 91.3899C295.67 89.27 299.44 84.2799 299.44 84.2799C299.45 84.27 299.46 84.2599 299.47 84.2399C301 82.4699 301.8 80.1799 301.78 77.8399C301.67 66.7899 301.57 55.74 301.46 44.7C253.98 51.83 198.85 56.94 137.5 56.9C88.7199 56.87 44.0799 53.5999 4.37988 48.7299H4.39988Z"
        fill="url(#mgmRGrad0)"
      />

      {/* Layer 2: Purple mid-layer accent */}
      <path
        d="M4.39988 39.0801V72.64C6.43988 75.66 9.81988 79.7201 15.0999 82.5C22.6999 86.5201 29.8699 85.5901 38.5399 85.1901C49.0999 84.7001 59.6699 86.25 70.2399 86.25C91.3099 86.25 112.38 86.28 133.45 86.25C148.6 86.23 163.52 86.25 174.18 86.25C190.52 86.26 198.69 86.2701 199.54 86.2901C235.44 86.8501 238.3 89.2801 251.03 86.2901C259.83 84.2201 262.98 82.0001 271.2 82.1201C278.99 82.2301 283.98 84.33 290.41 81.74C295.67 79.62 299.44 74.63 299.44 74.63C300.5 73.41 301.28 72.3 301.81 71.49C301.69 59.34 301.58 47.19 301.46 35.05C253.98 42.18 198.85 47.29 137.5 47.25C88.7199 47.22 44.0799 43.9501 4.37988 39.0801H4.39988Z"
        fill="url(#mgmRGrad1)"
      />

      {/* Layer 3: Green surface - main body */}
      <path
        d="M301.63 28.5899V54.9099C301.63 68.5299 290.59 79.5699 276.96 79.5699H40.2799C33.1899 79.5699 26.5799 77.5199 21.0199 73.9699C14.4799 69.7999 9.3799 63.5699 6.6399 56.1999C5.1899 52.2999 4.3999 48.0899 4.3999 43.6899V28.5899C4.3999 22.0599 6.9399 16.1299 11.0899 11.7099C15.0699 7.46993 20.5399 4.62993 26.6699 4.04993C27.4599 3.96993 28.2499 3.92993 29.0599 3.92993H276.96C282.52 3.92993 287.66 5.76993 291.79 8.88993C295.08 11.3599 297.73 14.6399 299.44 18.4399C300.85 21.5299 301.63 24.9699 301.63 28.5899Z"
        fill="url(#mgmRGrad2)"
      />

      {/* Layer 4: Green surface - top left corner facet */}
      <path
        d="M57.1799 4.05005C54.2999 6.06005 51.0699 8.34005 47.4899 10.95C36.0699 19.28 30.3599 23.44 26.5999 27.15C13.4199 40.14 12.5599 48.6701 6.6399 56.2001C5.1899 52.3 4.3999 48.09 4.3999 43.69V30.1C7.1099 25.39 8.4999 21.64 8.8299 20.73C10.1099 17.17 10.7199 14.23 11.0899 11.71C15.0699 7.47005 20.5399 4.63005 26.6699 4.05005H57.1799Z"
        fill="url(#mgmRGrad3)"
      />

      {/* Layer 5: Green surface - left side facet */}
      <path
        d="M92.46 4.05005C92.1 8.19005 90.61 13.28 87.94 17.62C83.64 24.6101 79.21 24.8601 71.94 30.9501C60.63 40.44 54.45 54.0201 52.38 64.2901C51.07 70.8 51.48 75.65 52.17 79.57H40.28C33.19 79.57 26.58 77.5201 21.02 73.97C28.47 69.53 30.83 68.0301 34.6 62.9501C44.63 49.4501 41.79 41.36 55.94 26.06C56.76 25.18 61.82 20.44 71.94 10.95C74.87 8.20005 77.32 5.92005 79.36 4.05005H92.46Z"
        fill="url(#mgmRGrad4)"
      />

      {/* Layer 6: Green surface - top right facet */}
      <path
        d="M299.44 18.44C297.6 20 295.05 22.05 289.95 25.07C281.45 30.1101 275.68 33.5301 267.44 34.75C255.62 36.4901 245.99 32.62 242.55 31.19C231.6 26.64 231.43 22.01 222.4 18.15C214.45 14.76 209.87 16.33 190.99 17.56C166.73 19.14 154.44 19.84 146.55 15.19C141.97 12.5 137.89 8.42005 134.94 4.05005H247.14C248.24 4.81005 249.46 5.56005 250.84 6.30005C265.64 14.2 281.55 11.44 285.21 10.71C287.65 10.22 289.83 9.60005 291.79 8.89005C295.08 11.36 297.73 14.64 299.44 18.44Z"
        fill="url(#mgmRGrad5)"
      />

      {/* Green sparkle decoration - top center wave */}
      <path
        d="M154.79 6.73013C154.55 8.17013 161.81 11.0001 168.57 12.2901C178.23 14.1201 182.74 12.0201 194.79 10.9601C209.57 9.66013 216.97 9.01013 225.01 12.2901C234.38 16.1101 235.67 21.3401 247.01 24.2901C248.57 24.7001 259.93 27.6601 262.12 23.8501C263.73 21.0501 259.86 15.4901 256.34 12.2901C249 5.63013 239.02 5.44013 229.67 5.40013C218.31 5.35013 218.85 7.49013 205.89 7.62013C196.37 7.72013 186.4 6.67013 182.78 6.29013C179.66 5.96013 173.21 5.22013 164.56 5.40013C159.59 5.50013 154.96 5.62013 154.78 6.73013H154.79Z"
        fill="#63F943"
      />

      {/* Green sparkle decoration - left side curve */}
      <path
        d="M80.1298 5.37002C77.7298 5.40002 77.3698 10.53 73.9098 15.57C69.7298 21.65 66.0598 20.32 59.4698 26.73C56.2998 29.82 54.3898 33.16 50.5798 39.84C46.7098 46.63 44.7498 51.79 42.3598 58.06C39.8598 64.63 38.6298 67.93 39.4698 68.5C40.9098 69.49 47.6698 64.31 51.2498 56.94C53.9298 51.44 52.2098 50.11 54.8098 43.61C55.8298 41.06 58.8798 33.69 65.9198 28.72C71.1098 25.05 72.7098 26.76 77.0298 22.94C81.1798 19.27 85.1998 12.84 83.2498 8.27002C82.7598 7.12002 81.5598 5.33002 80.1398 5.35002L80.1298 5.37002Z"
        fill="#63F943"
      />

      {/* Green sparkle decoration - top left corner */}
      <path
        d="M25.6797 8.29009C22.9797 11.7401 19.9297 10.9501 17.2397 13.4001C14.8397 15.5901 16.5797 16.8501 13.9097 23.4001C11.6897 28.8401 9.49973 30.4101 9.23973 35.0501C9.18973 35.9901 9.00973 39.2401 10.1297 39.6201C11.4197 40.0601 13.4097 36.4101 19.4597 27.6201C26.1197 17.9501 27.6797 16.6201 29.2397 15.5701C33.2597 12.8701 36.1197 12.8601 36.3497 10.9601C36.6997 8.12009 30.8597 4.05009 27.9097 5.38009C26.8297 5.87009 26.9197 6.72009 25.6897 8.30009L25.6797 8.29009Z"
        fill="#63F943"
      />

      {/* Dark inner shadow / depth band */}
      <path
        d="M17.1598 69.1398C29.4598 77.9998 57.1598 71.8898 95.3798 72.5298C127.75 73.0798 189.64 71.8098 204.57 73.1798C217.53 74.3698 263.05 74.3798 282.81 70.5298C285.1 70.0798 288.86 68.8198 291.25 66.3398C291.25 66.3398 292.69 61.8298 292.69 57.4798C292.69 48.0998 196.08 62.9498 89.5998 59.7198C55.0798 58.6698 14.7698 55.0898 14.7698 60.4998C14.7698 63.5498 14.3798 67.1498 17.1498 69.1498L17.1598 69.1398Z"
        fill="#2A1C35"
      />

      {/* Purple geometric frame - main body */}
      <path
        d="M280.38 37.06L272.68 40.9L291.91 45.94L291.99 45.96L292.7 46.15V61.65C292.7 61.93 292.67 62.2 292.63 62.46C292.1 65.6 288.27 67.57 277.47 68.31C277.37 68.31 277.27 68.33 277.17 68.33C274.05 68.53 270.37 68.64 266.03 68.64H42.59C25.2 68.64 17.85 67.14 15.84 64.01C15.49 63.46 15.3 62.87 15.25 62.22C15.25 62.1 15.23 61.98 15.23 61.86L16.66 59.83L21.79 52.56L35.47 51.89L15.24 49.64V14.77C15.24 10.17 21.83 8.26 42.82 8.26H167.69L176.34 11.27L168.82 14.02L182.86 11.04L200.45 8.25H261.23C266.92 8.25 271.85 8.34 276 8.57C281.27 8.86 285.3 9.36 288.07 10.17C291.15 11.07 292.69 12.34 292.69 14.1V35.84L281.98 41.83L280.37 37.06H280.38Z"
        fill="#886F97"
      />

      {/* Frame facet - top left corner A */}
      <path
        d="M31.2497 11.5299L25.5997 16.7499H15.4097V15.6099C15.4097 10.8799 18.1897 8.90991 27.0497 8.90991H27.5697L27.6897 8.98991L31.2597 11.5199L31.2497 11.5299Z"
        fill="#886F97"
      />

      {/* Frame facet - left edge band A */}
      <path d="M30.4597 23.5898H15.4097V16.7498H25.5997L30.4597 23.5898Z" fill="#7E6489" />

      {/* Frame facet - top left diamond */}
      <path
        d="M31.2496 11.5298L25.5996 16.7498L30.4596 23.5898L42.3996 15.2398L31.2496 11.5298Z"
        fill="#695875"
      />

      {/* Frame facet - top left upper */}
      <path
        d="M27.0396 8.90991L31.2495 11.5299L42.3996 15.2399L50.7295 12.0799L27.0396 8.90991Z"
        fill="#7E6489"
      />

      {/* Frame facet - left edge band B */}
      <path d="M15.3994 23.5898V30.8698L23.8194 33.7698L30.4594 23.5898H15.3994Z" fill="#695875" />

      {/* Frame facet - left mid A */}
      <path
        d="M36.3796 19.45L30.4595 23.59L27.0396 28.83L44.9696 23.16L36.3796 19.45Z"
        fill="#886F97"
      />

      {/* Frame facet - left mid B */}
      <path
        d="M23.8198 33.7697L27.0398 28.8297L34.4998 26.4697V30.3997L23.8198 33.7697Z"
        fill="#7E6489"
      />

      {/* Frame facet - left mid C */}
      <path
        d="M34.4995 26.4699L44.9695 23.1599L49.3695 24.3699L34.4995 30.3999V26.4699Z"
        fill="#695875"
      />

      {/* Frame facet - left upper B */}
      <path
        d="M36.3799 19.4497L42.3999 15.2397L55.7499 17.5597L44.9699 23.1597L36.3799 19.4497Z"
        fill="#7E6489"
      />

      {/* Frame facet - left lower A */}
      <path
        d="M15.3994 30.8699L23.8194 33.7699L26.4194 39.9799L15.3994 37.5199V30.8699Z"
        fill="#7E6489"
      />

      {/* Frame facet - left lower B */}
      <path
        d="M32.0798 31.1599V38.3899L26.4198 39.9799L23.8198 33.7699L32.0798 31.1599Z"
        fill="#695875"
      />

      {/* Frame facet - left lower C */}
      <path
        d="M26.4194 39.9798L27.9494 46.2798L15.4094 45.7498L15.3994 37.5198L26.4194 39.9798Z"
        fill="#695875"
      />

      {/* Frame facet - left lower D */}
      <path
        d="M32.0794 38.3899L39.6094 41.8999L27.9494 46.2799L26.4194 39.9799L32.0794 38.3899Z"
        fill="#886F97"
      />

      {/* Frame facet - left lower E */}
      <path
        d="M39.6095 41.8999L34.4995 48.4199L23.9395 53.8099L27.9495 46.2799L39.6095 41.8999Z"
        fill="#695875"
      />

      {/* Frame facet - bottom left A */}
      <path
        d="M23.1697 50.4199L20.1397 60.8599L15.2397 58.5899L17.3997 51.0999L23.1697 50.4199Z"
        fill="#695875"
      />

      {/* Frame facet - bottom right cluster A */}
      <path
        d="M276.24 63.8298L267.91 66.9998L261.72 68.6798H257.41L262.89 61.5098L276.24 63.8298Z"
        fill="#886F97"
      />

      {/* Frame facet - bottom right cluster B */}
      <path
        d="M261.54 68.6798L256.3 64.0198L245.09 62.5798L249.52 68.6798H261.54Z"
        fill="#7E6489"
      />

      {/* Frame facet - bottom right cluster C */}
      <path d="M261 59.45H247.95L245.09 62.58L256.3 64.0199L261 59.45Z" fill="#695875" />

      {/* Frame facet - bottom right cluster D */}
      <path
        d="M245.089 62.5797L233.709 61.7397L234.649 68.6797H249.519L245.089 62.5797Z"
        fill="#695875"
      />

      {/* Frame facet - bottom right cluster E */}
      <path
        d="M234.65 68.6797H224.28L220.1 63.9497L233.71 61.7397L234.65 68.6797Z"
        fill="#7E6489"
      />

      {/* Frame facet - bottom right cluster F */}
      <path
        d="M241.62 55.2798L229.84 58.1098L220.1 63.9498L233.71 61.7398L241.62 55.2798Z"
        fill="#695875"
      />

      {/* Frame facet - bottom right cluster G */}
      <path
        d="M220.1 63.95L201.25 65.6299L205.35 68.3499L218.87 67.15L220.1 63.95Z"
        fill="#695875"
      />

      {/* Frame facet - bottom mid cluster A */}
      <path
        d="M222.019 63.8298L213.689 66.9998L207.499 68.6798H203.189L208.669 61.5098L222.019 63.8298Z"
        fill="#886F97"
      />

      {/* Frame facet - bottom mid cluster B */}
      <path
        d="M207.32 68.6798L202.08 64.0198L190.87 62.5798L195.3 68.6798H207.32Z"
        fill="#7E6489"
      />

      {/* Frame facet - bottom mid cluster C */}
      <path d="M206.78 59.45H193.73L190.87 62.58L202.08 64.0199L206.78 59.45Z" fill="#695875" />

      {/* Frame facet - bottom mid cluster D */}
      <path
        d="M190.87 62.5797L179.49 61.7397L180.43 68.6797H195.3L190.87 62.5797Z"
        fill="#695875"
      />

      {/* Frame facet - bottom mid cluster E */}
      <path
        d="M180.43 68.6797H170.05L165.88 63.9497L179.49 61.7397L180.43 68.6797Z"
        fill="#7E6489"
      />

      {/* Frame facet - bottom mid cluster F */}
      <path
        d="M187.4 55.2798L175.62 58.1098L165.88 63.9498L179.49 61.7398L187.4 55.2798Z"
        fill="#695875"
      />

      {/* Frame facet - bottom mid cluster G */}
      <path
        d="M165.88 63.95L147.03 65.6299L151.13 68.3499L164.64 67.15L165.88 63.95Z"
        fill="#695875"
      />

      {/* Frame facet - bottom left cluster A */}
      <path
        d="M130.03 68.6797H119.65L115.47 63.9497L129.08 61.7397L130.03 68.6797Z"
        fill="#7E6489"
      />

      {/* Frame facet - bottom left cluster B */}
      <path
        d="M136.99 55.2798L125.21 58.1098L115.47 63.9498L129.08 61.7398L136.99 55.2798Z"
        fill="#695875"
      />

      {/* Frame facet - bottom left cluster C */}
      <path
        d="M115.47 63.95L96.6196 65.6299L100.73 68.3499L114.24 67.15L115.47 63.95Z"
        fill="#695875"
      />

      {/* Frame facet - bottom left cluster D */}
      <path
        d="M117.39 63.8298L109.06 66.9998L102.87 68.6798H98.5596L104.04 61.5098L117.39 63.8298Z"
        fill="#886F97"
      />

      {/* Frame facet - bottom left cluster E */}
      <path
        d="M102.69 68.6798L97.4497 64.0198L86.2397 62.5798L90.6697 68.6798H102.69Z"
        fill="#7E6489"
      />

      {/* Frame facet - bottom left cluster F */}
      <path d="M102.15 59.45H89.0997L86.2397 62.58L97.4497 64.0199L102.15 59.45Z" fill="#695875" />

      {/* Frame facet - bottom left cluster G */}
      <path
        d="M86.2399 62.5797L74.8599 61.7397L75.7999 68.6797H90.6699L86.2399 62.5797Z"
        fill="#695875"
      />

      {/* Frame facet - bottom left cluster H */}
      <path
        d="M75.7995 68.6797H65.4295L61.2495 63.9497L74.8595 61.7397L75.7995 68.6797Z"
        fill="#7E6489"
      />

      {/* Frame facet - bottom left cluster I */}
      <path
        d="M82.7695 55.2798L70.9895 58.1098L61.2495 63.9498L74.8595 61.7398L82.7695 55.2798Z"
        fill="#695875"
      />

      {/* Frame facet - bottom left cluster J */}
      <path
        d="M61.2494 63.95L42.3994 65.6299L46.4994 68.3499L60.0194 67.15L61.2494 63.95Z"
        fill="#695875"
      />

      {/* Frame facet - top left cluster A */}
      <path
        d="M58.2295 8.53979H68.6095L72.7795 13.2698L59.1695 15.4798L58.2295 8.53979Z"
        fill="#7E6489"
      />

      {/* Frame facet - top left cluster B */}
      <path
        d="M51.2598 21.9398L63.0398 19.1098L72.7798 13.2698L59.1698 15.4798L51.2598 21.9398Z"
        fill="#695875"
      />

      {/* Frame facet - top left cluster C */}
      <path
        d="M72.7798 13.2699L91.6298 11.5899L87.5298 8.86987L74.0198 10.0699L72.7798 13.2699Z"
        fill="#695875"
      />

      {/* Frame facet - top left cluster D */}
      <path
        d="M70.8599 13.3898L79.1899 10.2198L85.3799 8.53979H89.6999L84.2099 15.7098L70.8599 13.3898Z"
        fill="#886F97"
      />

      {/* Frame facet - top left cluster E */}
      <path
        d="M85.5596 8.53979L90.7996 13.1898L102.01 14.6398L97.5796 8.53979H85.5596Z"
        fill="#7E6489"
      />

      {/* Frame facet - top left cluster F */}
      <path
        d="M86.0996 17.7699H99.1496L102.01 14.6399L90.7996 13.1899L86.0996 17.7699Z"
        fill="#695875"
      />

      {/* Frame facet - top left cluster G */}
      <path
        d="M102.01 14.6398L113.39 15.4798L112.45 8.53979H97.5796L102.01 14.6398Z"
        fill="#695875"
      />

      {/* Frame facet - top left cluster H */}
      <path d="M112.45 8.53979H122.83L127 13.2698L113.39 15.4798L112.45 8.53979Z" fill="#7E6489" />

      {/* Frame facet - top left cluster I */}
      <path
        d="M105.49 21.9398L117.27 19.1098L127 13.2698L113.39 15.4798L105.49 21.9398Z"
        fill="#695875"
      />

      {/* Frame facet - top left cluster J */}
      <path
        d="M127 13.2699L145.85 11.5899L141.75 8.86987L128.24 10.0699L127 13.2699Z"
        fill="#695875"
      />

      {/* Frame facet - top right cluster A */}
      <path
        d="M195.3 13.1898L203.63 10.0198L209.82 8.33984H214.13L208.65 15.5098L195.3 13.1898Z"
        fill="#886F97"
      />

      {/* Frame facet - top right cluster B */}
      <path d="M210 8.33984L215.24 12.9898L226.45 14.4398L222.02 8.33984H210Z" fill="#7E6489" />

      {/* Frame facet - top right cluster C */}
      <path
        d="M210.54 17.5697H223.59L226.45 14.4397L215.24 12.9897L210.54 17.5697Z"
        fill="#695875"
      />

      {/* Frame facet - top right cluster D */}
      <path
        d="M226.45 14.4398L237.83 15.2798L236.89 8.33984H222.02L226.45 14.4398Z"
        fill="#695875"
      />

      {/* Frame facet - top right cluster E */}
      <path
        d="M236.89 8.33984H247.27L251.44 13.0698L237.83 15.2798L236.89 8.33984Z"
        fill="#7E6489"
      />

      {/* Frame facet - top right cluster F */}
      <path
        d="M229.919 21.7398L241.699 18.9098L251.439 13.0698L237.829 15.2798L229.919 21.7398Z"
        fill="#695875"
      />

      {/* Frame facet - top right cluster G */}
      <path
        d="M251.439 13.0699L270.289 11.3899L266.189 8.66992L252.679 9.86992L251.439 13.0699Z"
        fill="#695875"
      />

      {/* Frame corner - bottom left */}
      <path
        d="M27.1796 67.6498H26.1796C18.8396 67.6498 15.7396 66.1098 14.8896 62.8898C14.7396 62.3298 14.6596 61.7098 14.6396 61.0498L15.2396 58.5898L21.7196 61.6398L27.1796 67.6498Z"
        fill="#886F97"
      />

      {/* Frame corner - bottom right A */}
      <path
        d="M292.669 61.9599C292.459 65.0099 290.929 66.9299 286.629 67.6399C286.589 67.6399 286.549 67.65 286.509 67.66L285.419 66.8799L290.759 61.95H292.679L292.669 61.9599Z"
        fill="#886F97"
      />

      {/* Frame corner - bottom right B */}
      <path
        d="M292.7 55.4897V61.1597C292.7 61.4297 292.69 61.6897 292.67 61.9497H290.75L286.16 55.4897H292.69H292.7Z"
        fill="#7E6489"
      />

      {/* Frame corner - bottom right C */}
      <path
        d="M285.42 66.8797L290.76 61.9597L286.16 55.4897L274.89 63.3797L285.42 66.8797Z"
        fill="#695875"
      />

      {/* Frame corner - bottom right D */}
      <path
        d="M286.63 67.6399C286.63 67.6399 286.55 67.6499 286.51 67.6599C285.27 67.8599 283.8 67.9599 282.07 67.9599H278.92L267.03 66.3699L274.89 63.3799L285.42 66.8799L286.63 67.6399Z"
        fill="#7E6489"
      />

      {/* Frame right side - mid A */}
      <path
        d="M15.4097 45.7498V51.4998L23.9397 53.8098L27.9497 46.2798L15.4097 45.7498Z"
        fill="#7E6489"
      />

      {/* Frame right side - mid B */}
      <path
        d="M292.7 46.0999V55.4899H286.16L289.39 50.5499L292.41 45.9199L292.7 46.0999Z"
        fill="#695875"
      />

      {/* Frame right side - mid C */}
      <path
        d="M280.579 59.3998L286.159 55.4898L289.389 50.5498L272.459 55.9098L280.579 59.3998Z"
        fill="#886F97"
      />

      {/* Frame right side - mid D */}
      <path
        d="M292.44 45.8799L292.41 45.9199L289.39 50.5499L282.34 52.7799V49.0599L292.38 45.8999L292.44 45.8799Z"
        fill="#7E6489"
      />

      {/* Frame right side - mid E */}
      <path
        d="M282.35 52.7798L272.46 55.9098L268.31 54.7598L282.35 49.0598V52.7798Z"
        fill="#695875"
      />

      {/* Frame right side - mid F */}
      <path
        d="M280.58 59.3999L274.89 63.3799L262.28 61.1899L272.46 55.9099L280.58 59.3999Z"
        fill="#7E6489"
      />

      {/* Frame right side - upper A */}
      <path
        d="M292.7 36.1298L284.75 33.3898L282.29 27.5298L292.7 29.8498V36.1298Z"
        fill="#7E6489"
      />

      {/* Frame right side - upper B */}
      <path
        d="M276.95 35.8498V29.0298L282.29 27.5298L284.75 33.3898L276.95 35.8498Z"
        fill="#695875"
      />

      {/* Frame right side - upper C */}
      <path
        d="M282.29 27.5298L280.85 21.5798L292.7 22.0698V29.8498L282.29 27.5298Z"
        fill="#695875"
      />

      {/* Frame right side - upper D */}
      <path d="M292.7 22.07V16.64L284.63 14.46L280.85 21.58L292.7 22.07Z" fill="#7E6489" />

      {/* Frame right side - upper E */}
      <path d="M269.84 25.71L274.66 19.55L284.63 14.46L280.85 21.58L269.84 25.71Z" fill="#695875" />

      {/* Frame right corner - top A */}
      <path
        d="M290.86 11.1499L290.09 13.8199L284.64 14.4699L286.05 9.59985C288.15 9.87985 289.75 10.3699 290.86 11.1499Z"
        fill="#695875"
      />

      {/* Frame right corner - top B */}
      <path
        d="M271.57 9.21973L280.37 9.27973L277.23 14.7997L274.17 13.5297L271.57 9.21973Z"
        fill="#7E6489"
      />

      {/* Dynamic player pseudo text */}
      <g clipPath="url(#mgmRTextClip)">
        <text
          x={PSEUDO_BOX_CONFIG.TEXT.RIGHT_X_POSITION}
          y={PSEUDO_BOX_CONFIG.TEXT.Y_POSITION}
          fill="#FFFFFF"
          fontSize={PSEUDO_BOX_CONFIG.TEXT.FONT_SIZE}
          fontWeight="bold"
          textAnchor="end"
          dominantBaseline="middle"
          stroke="#000000"
          strokeWidth="2"
          strokeLinejoin="round"
          paintOrder="stroke"
        >
          {displayText}
        </text>
      </g>
    </svg>
  );
});

export default MagmaRightPseudoBox;
